import { Tables } from "@/types/database.types";
import { User } from "@supabase/supabase-js";

/**
 * Represents the possible redirect paths in the application
 */
export type RedirectPath =
  | "/"
  | "/ai-assistants"
  | "/pricing"
  | "/user-onboarding"
  | "/payment"
  | "/thank-you";

/**
 * Contains the redirect decision and reasoning for post-authentication flows
 */
export interface RedirectDecision {
  path: RedirectPath;
  reason: string;
}

/**
 * Union type for different user object shapes that can be passed to redirect service
 */
export type AuthUser =
  | Tables<"profiles">
  | User
  | {
      id: string;
      email?: string;
      user_metadata?: Record<string, unknown>;
    };

export const redirectService = {
  /**
   * Determines where to redirect after authentication based on user state
   */
  async getPostAuthRedirect(
    user: AuthUser,
    organization: Tables<"organizations"> | null,
    isNewUser: boolean = false,
  ): Promise<RedirectDecision> {
    console.log("[RedirectService] Determining post-auth redirect:", {
      userId: user?.id,
      organizationId: organization?.id,
      subscriptionStatus: organization?.subscription_status,
      onboardingMeta: organization?.onboarding_meta,
      isNewUser,
      hasOrganization: !!organization,
    });

    // 1. New users always go to onboarding
    if (isNewUser) {
      console.log(
        "[RedirectService] New user detected, redirecting to onboarding",
      );
      return {
        path: "/user-onboarding",
        reason: "New user signup",
      };
    }

    // Also check for the session storage flag as backup
    const newUserOnboarding =
      typeof window !== "undefined" &&
      sessionStorage.getItem("newUserOnboarding") === "true";

    if (newUserOnboarding) {
      console.log(
        "[RedirectService] Found newUserOnboarding flag, redirecting to onboarding",
      );
      return {
        path: "/user-onboarding",
        reason: "New user onboarding flag detected",
      };
    }

    // 2. No organization - for new users, this might be a timing issue
    if (!organization) {
      console.log("[RedirectService] No organization found");

      // Check if this might be a new user with delayed organization creation
      const pendingNewUserRedirect =
        typeof window !== "undefined" &&
        sessionStorage.getItem("pendingNewUserRedirect") === "true";

      if (pendingNewUserRedirect) {
        console.log(
          "[RedirectService] Pending new user with missing organization, redirecting to onboarding anyway",
        );
        return {
          path: "/user-onboarding",
          reason: "New user with delayed organization creation",
        };
      }

      console.log(
        "[RedirectService] No organization found, redirecting to onboarding",
      );
      return {
        path: "/user-onboarding",
        reason: "No organization found",
      };
    }

    // 3. Has active or trialing subscription - go to home
    if (
      organization.subscription_status === "active" ||
      organization.subscription_status === "trialing"
    ) {
      console.log(
        "[RedirectService] Active/trialing subscription, redirecting to AI assistants",
      );
      return {
        path: "/ai-assistants",
        reason: "Active subscription",
      };
    }

    // 4. Has canceled or past_due subscription - go to pricing
    if (
      organization.subscription_status &&
      [
        "canceled",
        "past_due",
        "incomplete",
        "incomplete_expired",
        "unpaid",
        "paused",
      ].includes(organization.subscription_status)
    ) {
      console.log(
        "[RedirectService] Inactive subscription, redirecting to pricing",
      );
      return {
        path: "/pricing",
        reason: "Inactive subscription",
      };
    }

    // 5. No subscription status - check onboarding completion
    interface OnboardingMeta {
      completed?: boolean;
      current_step?: number;
      completed_steps?: number[];
    }

    const onboardingMeta =
      organization.onboarding_meta as OnboardingMeta | null;
    const isOnboardingCompleted = onboardingMeta?.completed === true;

    console.log(
      "[RedirectService] No subscription status, checking onboarding:",
      {
        onboardingMeta,
        isCompleted: isOnboardingCompleted,
      },
    );

    if (isOnboardingCompleted) {
      console.log(
        "[RedirectService] Onboarding complete but no subscription, redirecting to payment",
      );
      return {
        path: "/payment",
        reason: "Onboarding complete, no subscription",
      };
    } else {
      console.log(
        "[RedirectService] Incomplete onboarding, redirecting to onboarding",
      );
      return {
        path: "/user-onboarding",
        reason: "Incomplete onboarding",
      };
    }
  },

  /**
   * Store intended destination before redirecting to auth
   */
  setIntendedDestination(path: string) {
    console.log("[RedirectService] Storing intended destination:", path);
    if (typeof window !== "undefined") {
      sessionStorage.setItem("redirectAfterAuth", path);
    }
  },

  /**
   * Get and clear intended destination
   */
  getAndClearIntendedDestination(): string | null {
    if (typeof window === "undefined") {
      return null;
    }

    const path = sessionStorage.getItem("redirectAfterAuth");
    if (path) {
      console.log(
        "[RedirectService] Retrieved and clearing intended destination:",
        path,
      );
      sessionStorage.removeItem("redirectAfterAuth");
    }
    return path;
  },
};
